import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  CalendarRange,
  List,
  Plus,
  Sparkles,
  Trash2,
} from 'lucide-react';
import {
  canProject,
  TRAINING_MAX_SESSIONS,
  TRAINING_SESSION_STATUS_LABEL,
  type TrainingSession,
} from '@allemnii-pulse/shared';
import { useProject } from '../projects/ProjectProvider.js';
import { api, ApiError } from '../../lib/api.js';
import { Card } from '../../components/ui/Card.js';
import { Button } from '../../components/ui/Button.js';
import { Input } from '../../components/ui/Input.js';
import { Textarea } from '../../components/ui/Textarea.js';
import { Label } from '../../components/ui/Label.js';
import { DatePicker } from '../../components/ui/DatePicker.js';
import { GenerateDialog, type GenerateRequest } from './GenerateDialog.js';
import { SessionsTimeline } from './SessionsTimeline.js';
import { TrainingExportMenu } from './TrainingExportMenu.js';
import { postJob, useJobRunner, useTrainingSessions, useTrainingState } from './useTraining.js';

/** One editable row: a session's plan, with learning objectives as one line each. */
interface Row {
  id?: string;
  key: string;
  title: string;
  summary: string;
  objectives: string;
  durationMinutes: string;
  slideCount: string;
  date: string | null;
}

function toRow(s: TrainingSession): Row {
  return {
    id: s.id,
    key: s.id,
    title: s.title,
    summary: s.summary,
    objectives: s.learningObjectives.join('\n'),
    durationMinutes: String(s.durationMinutes),
    slideCount: String(s.slideCount),
    date: s.date,
  };
}

function blankRow(): Row {
  return {
    key: `new-${Math.random().toString(36).slice(2)}`,
    title: '',
    summary: '',
    objectives: '',
    durationMinutes: '45',
    slideCount: '8',
    date: null,
  };
}

function rowProblem(r: Row): string | null {
  if (!r.title.trim()) return 'Every session needs a title.';
  if (!r.summary.trim()) return `"${r.title}" needs a summary.`;
  if (!r.objectives.trim()) return `"${r.title}" needs at least one learning objective.`;
  const d = Number(r.durationMinutes);
  if (!Number.isInteger(d) || d < 5 || d > 480)
    return `"${r.title}": duration must be 5–480 minutes.`;
  const c = Number(r.slideCount);
  if (!Number.isInteger(c) || c < 1 || c > 40) return `"${r.title}": slide count must be 1–40.`;
  return null;
}

function StatusChip({ session }: { session: TrainingSession }) {
  if (session.stale) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-danger/10 px-2 py-0.5 text-[11px] font-medium text-danger">
        <AlertTriangle className="h-3 w-3" /> Needs review
      </span>
    );
  }
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
        session.status === 'ready'
          ? 'bg-success/15 text-success'
          : 'bg-accent-soft text-accent-soft-fg'
      }`}
    >
      {TRAINING_SESSION_STATUS_LABEL[session.status]}
    </span>
  );
}

/**
 * Step two: the sessions list. Generated by Gemini from the sources and answers, edited by hand
 * (rename, reorder, add, delete, dates), regenerated with feedback until confirmed — after
 * which it can still be edited by hand, but no longer regenerated wholesale, so outlines and
 * slides are never thrown away by a re-roll. A list/timeline toggle switches the view.
 */
export function TrainingSessionsPage() {
  const { projectId, role } = useProject();
  const canManage = canProject(role, 'training.manage');
  const { data: state } = useTrainingState(projectId);
  const { data: sessions, loading } = useTrainingSessions(projectId);
  const [params, setParams] = useSearchParams();
  const view = params.get('view') === 'timeline' ? 'timeline' : 'list';

  const [rows, setRows] = useState<Row[]>([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'generate' | 'regenerate' | null>(null);
  const generation = useJobRunner(() => setDirty(false));

  useEffect(() => {
    if (!dirty) setRows(sessions.map(toRow));
  }, [sessions, dirty]);

  const confirmed = !!state?.sessionsConfirmedAt;
  const questionsPending = !!state && state.questions.length > 0 && !state.questionsAnsweredAt;
  const byId = new Map(sessions.map((s) => [s.id, s]));

  function update(key: string, patch: Partial<Row>) {
    setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
    setDirty(true);
  }
  function move(index: number, delta: number) {
    setRows((rs) => {
      const next = [...rs];
      const [row] = next.splice(index, 1);
      next.splice(index + delta, 0, row!);
      return next;
    });
    setDirty(true);
  }
  function remove(row: Row) {
    const s = row.id ? byId.get(row.id) : undefined;
    if (
      s?.currentDeckVersionId &&
      !window.confirm(`Delete "${s.title}" and its slides? This can't be undone.`)
    )
      return;
    setRows((rs) => rs.filter((r) => r.key !== row.key));
    setDirty(true);
  }

  async function save(): Promise<boolean> {
    const problem =
      rows.length === 0 ? 'Keep at least one session.' : rows.map(rowProblem).find(Boolean);
    if (problem) {
      setError(problem);
      return false;
    }
    setSaving(true);
    setError(null);
    try {
      await api.put(`/projects/${projectId}/training/sessions`, {
        sessions: rows.map((r) => ({
          ...(r.id ? { id: r.id } : {}),
          title: r.title.trim(),
          summary: r.summary.trim(),
          learningObjectives: r.objectives
            .split('\n')
            .map((l) => l.trim())
            .filter(Boolean),
          durationMinutes: Number(r.durationMinutes),
          slideCount: Number(r.slideCount),
          date: r.date,
        })),
      });
      setDirty(false);
      return true;
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save the sessions.');
      return false;
    } finally {
      setSaving(false);
    }
  }

  async function confirm() {
    if (dirty && !(await save())) return;
    try {
      await api.post(`/projects/${projectId}/training/sessions/confirm`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not confirm the sessions.');
    }
  }

  function generate(request: GenerateRequest) {
    if (dirty && !window.confirm('Regenerating replaces your unsaved edits. Continue?')) return;
    void generation.start(
      () => postJob(projectId, '/sessions/generate', request),
      'Could not start generating the sessions.',
    );
  }

  const editable = canManage && view === 'list';

  return (
    <div className="flex flex-col gap-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="font-heading text-2xl font-semibold">Sessions</h1>
          <p className="mt-1 text-sm text-muted">
            {confirmed
              ? 'Confirmed. Open a session to outline it and build its slides.'
              : 'Review the plan, edit it, then confirm it to start outlining.'}
          </p>
        </div>
        <div className="flex rounded-lg border border-border p-0.5">
          <Button
            size="sm"
            variant={view === 'list' ? 'secondary' : 'ghost'}
            onClick={() => setParams({})}
          >
            <List /> List
          </Button>
          <Button
            size="sm"
            variant={view === 'timeline' ? 'secondary' : 'ghost'}
            onClick={() => setParams({ view: 'timeline' })}
          >
            <CalendarRange /> Timeline
          </Button>
        </div>
        <TrainingExportMenu
          projectId={projectId}
          label="Export all"
          disabled={!sessions.some((s) => s.currentDeckVersionId)}
        />
      </div>

      {generation.running && <Card className="p-4 text-sm text-muted">Planning the sessions…</Card>}
      {generation.error && (
        <Card className="border-danger p-4 text-sm text-danger">{generation.error}</Card>
      )}

      {!loading && sessions.length === 0 && !generation.running && (
        <Card className="flex flex-col items-start gap-3 p-4 sm:p-6">
          {!state?.analyzedAt ? (
            <p className="text-sm text-muted">
              Add and analyse the sources first —{' '}
              <Link to={`/projects/${projectId}/sources`} className="text-accent">
                go to Sources
              </Link>
              .
            </p>
          ) : questionsPending ? (
            <p className="text-sm text-muted">
              Answer the follow-up questions first —{' '}
              <Link to={`/projects/${projectId}/sources`} className="text-accent">
                go to Sources
              </Link>
              .
            </p>
          ) : (
            <>
              <p className="text-sm text-muted">
                {state.minSessions || state.maxSessions
                  ? `Gemini will plan ${state.minSessions ?? 1}–${state.maxSessions ?? 'any number of'} sessions.`
                  : 'Gemini will choose the number of sessions from the topic’s complexity.'}{' '}
                Set a minimum or maximum on the Sources tab.
              </p>
              {canManage && (
                <Button variant="default" onClick={() => setDialog('generate')}>
                  <Sparkles /> Generate sessions
                </Button>
              )}
            </>
          )}
        </Card>
      )}

      {view === 'timeline' ? (
        <SessionsTimeline projectId={projectId} sessions={sessions} />
      ) : (
        rows.length > 0 && (
          <div className="flex flex-col gap-3">
            {rows.map((row, i) => {
              const s = row.id ? byId.get(row.id) : undefined;
              return (
                <Card key={row.key} className="flex flex-col gap-3 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-xs text-muted">{i + 1}</span>
                    <Input
                      value={row.title}
                      maxLength={200}
                      disabled={!editable}
                      placeholder="Session title"
                      onChange={(e) => update(row.key, { title: e.target.value })}
                      className="h-auto min-w-[200px] flex-1 border-none bg-transparent px-1 text-base font-semibold"
                    />
                    {s && confirmed && <StatusChip session={s} />}
                    {s && confirmed && (
                      <Link
                        to={`/projects/${projectId}/sessions/${s.id}`}
                        className="inline-flex h-10 items-center text-sm font-medium text-accent md:h-auto"
                      >
                        Open →
                      </Link>
                    )}
                    {editable && (
                      // Wraps under the title on a phone; ml-auto then right-aligns it (beside
                      // the flex-1 title on wider screens it has no effect).
                      <div className="ml-auto flex">
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Move up"
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                          className="h-10 w-10 md:h-8 md:w-8"
                        >
                          <ArrowUp />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Move down"
                          disabled={i === rows.length - 1}
                          onClick={() => move(i, 1)}
                          className="h-10 w-10 md:h-8 md:w-8"
                        >
                          <ArrowDown />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          aria-label="Delete session"
                          onClick={() => remove(row)}
                          className="h-10 w-10 md:h-8 md:w-8"
                        >
                          <Trash2 />
                        </Button>
                      </div>
                    )}
                  </div>
                  {s?.stale && <p className="text-xs text-danger">{s.stale.reason}</p>}
                  <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`summary-${row.key}`}>Summary</Label>
                      <Textarea
                        id={`summary-${row.key}`}
                        rows={4}
                        maxLength={2000}
                        disabled={!editable}
                        value={row.summary}
                        onChange={(e) => update(row.key, { summary: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5">
                      <Label htmlFor={`objectives-${row.key}`}>
                        Learning objectives (one per line)
                      </Label>
                      <Textarea
                        id={`objectives-${row.key}`}
                        rows={4}
                        disabled={!editable}
                        value={row.objectives}
                        onChange={(e) => update(row.key, { objectives: e.target.value })}
                      />
                    </div>
                  </div>
                  {/* Two columns on a phone (duration and slides side by side, the date below). */}
                  <div className="grid grid-cols-2 gap-3 sm:flex sm:flex-wrap">
                    <div className="flex flex-col gap-1.5 sm:w-32">
                      <Label htmlFor={`duration-${row.key}`}>Duration (min)</Label>
                      <Input
                        id={`duration-${row.key}`}
                        type="number"
                        min={5}
                        max={480}
                        disabled={!editable}
                        value={row.durationMinutes}
                        onChange={(e) => update(row.key, { durationMinutes: e.target.value })}
                      />
                    </div>
                    <div className="flex flex-col gap-1.5 sm:w-32">
                      <Label htmlFor={`slides-${row.key}`}>Slides</Label>
                      <Input
                        id={`slides-${row.key}`}
                        type="number"
                        min={1}
                        max={40}
                        disabled={!editable}
                        value={row.slideCount}
                        onChange={(e) => update(row.key, { slideCount: e.target.value })}
                      />
                    </div>
                    <div className="col-span-2 flex flex-col gap-1.5 sm:w-56">
                      <Label htmlFor={`date-${row.key}`}>Date</Label>
                      {editable ? (
                        <DatePicker
                          id={`date-${row.key}`}
                          value={row.date}
                          onChange={(date) => update(row.key, { date })}
                          onClear={() => update(row.key, { date: null })}
                        />
                      ) : (
                        <span className="text-sm text-muted">{row.date ?? 'Not scheduled'}</span>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        )
      )}

      {error && <p className="text-sm text-danger">{error}</p>}

      {editable && rows.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          <Button
            disabled={rows.length >= TRAINING_MAX_SESSIONS}
            onClick={() => {
              setRows((rs) => [...rs, blankRow()]);
              setDirty(true);
            }}
          >
            <Plus /> Add session
          </Button>
          {!confirmed && (
            <Button disabled={generation.running} onClick={() => setDialog('regenerate')}>
              <Sparkles /> Regenerate with feedback
            </Button>
          )}
          <div className="ml-auto flex flex-wrap justify-end gap-2">
            {dirty && (
              <Button variant="ghost" onClick={() => setDirty(false)}>
                Discard changes
              </Button>
            )}
            <Button
              variant={confirmed ? 'default' : 'secondary'}
              disabled={!dirty || saving}
              onClick={() => void save()}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
            {!confirmed && (
              <Button
                variant="default"
                disabled={saving || generation.running}
                onClick={() => void confirm()}
              >
                Confirm sessions
              </Button>
            )}
          </div>
        </div>
      )}

      {state && (
        <GenerateDialog
          open={dialog !== null}
          onClose={() => setDialog(null)}
          title={dialog === 'regenerate' ? 'Regenerate the sessions' : 'Generate the sessions'}
          description="Gemini plans the sessions from every source and your answers."
          submitLabel={dialog === 'regenerate' ? 'Regenerate' : 'Generate'}
          feedback={dialog === 'regenerate' ? 'required' : 'none'}
          defaults={state.instructions}
          onSubmit={generate}
        />
      )}
    </div>
  );
}
